import PubSub from "pubsub-js";
import DataApi from "../../api/dataApi";
import { name } from "pubsub-js";
import { createModel } from "../common/UniversalCSVXLSXImporter";

const downloadState = { isLoading: false };

const normalizeListResponse = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
    if (Array.isArray(payload.records)) return payload.records;
    if (Array.isArray(payload.rows)) return payload.rows;
    if (payload.results && Array.isArray(payload.results)) return payload.results;
    if (payload.book_copies && Array.isArray(payload.book_copies)) return payload.book_copies;
    return [];
};

export const getBooksConfig = (externalData = {}, props = {}, permissions = {}, shelf = {}) => {
    const authors = props.authors || externalData.authors || externalData.author || [];
    const classifications = props.classifications || externalData.classifications || externalData.category || [];
    const publishers = props.publishers || externalData.publishers || externalData.publisher || [];
    const groupedShelves = props.groupedShelves || externalData.groupedShelves || [];
    const branches = props.branches || externalData.branches || [];

    const company = externalData.company || {};
    const isBookBankEnabled = company.book_bank === true;

    const authorOptions = authors.map(a => ({
        value: a.id,
        label: a.name
    }));

 // At the top of bookconfig.js
const classificationOptions = classifications.map(c => ({
    value: String(c.id), // 🌟 FIX: Force string to prevent blank UI mismatches
    label: `${c.category || ''} - ${c.name} (${c.code})`,
    data: c
}));
    const publisherOptions = publishers.map(p => ({
        value: String(p.id),
        label: p.name
    }));

    const BookModel = createModel({
        modelName: "Book",
        fields: {
            title: "Title",
            author_id: "Author",
            classification_id: "Classification",
            publisher_id: "Publisher",
            isbn: "ISBN/ISSN",
            edition: "Edition",
            publication_year: "Publication Year",
            language: "Language",
            pages: "Pages",
            total_copies: "Total Copies",
            available_copies: "Available Copies",
            min_age: "Min Age",
            year_of_edition: "Year Of Edition",
            volume: "Volume",
        },
        required: ["title", "author_id", "classification_id"]
    });

    const inventoryBindings = [
        { value: "hardcover", label: "Hardcover" },
        { value: "paperback", label: "Paperback" },
        { value: "spiral", label: "Spiral" },
    ];

    const bulkDownloadBarcodes = async (selectedBooks) => {
        const books = Array.isArray(selectedBooks)
        ? [...new Map(selectedBooks.filter(b => b?.id).map(b => [b.id, b])).values()]
        : [];

        if (!books.length) {
            PubSub.publish("RECORD_INFO_TOAST", {
                title: "No Valid Books",
                message: "Please select valid books (check browser console)."
            });
            return;
        }

        try {
            const { jsPDF } = await import("jspdf");
            const JsBarcode = (await import("jsbarcode")).default;

            const doc = new jsPDF("p", "mm", "a4");

            const startX = 10;
            const startY = 20;
            const columns = 5; 
            const blockWidth = 38; 
            const blockHeight = 36;

            let bookIndex = 0;
            const allCopiesByBook = {};
            const globalBarcodeSet = new Set();

            for (const book of books) {
                if (!book || !book.id) continue;
                try {
                    const copyApi = new DataApi("book-copy");
                    const copiesResponse = await copyApi.get(`/book/${book.id}?page=1&limit=5000`);
                    const copies = normalizeListResponse(copiesResponse.data) || [];

                    copies.forEach((copy) => {
                        if (!copy?.barcode || globalBarcodeSet.has(copy.barcode)) return;
                        
                        globalBarcodeSet.add(copy.barcode);
                        const bookTitle = book.title || "Untitled";

                        if (!allCopiesByBook[bookTitle]) {
                            allCopiesByBook[bookTitle] = new Set();
                        }
                        allCopiesByBook[bookTitle].add(copy.barcode);
                    });
                } catch (copyError) {
                    console.error(`Failed to fetch copies for book ${book.id}:`, copyError);
                }
            }

            Object.entries(allCopiesByBook).forEach(([bookTitle, barcodeSet]) => {
                const barcodes = Array.from(barcodeSet);

                barcodes.forEach((barcode) => {
                    const col = bookIndex % columns;
                    const row = Math.floor(bookIndex / columns);

                    let x = startX + col * blockWidth;
                    let y = startY + row * blockHeight;

                    if (y > 260) {
                        doc.addPage();
                        bookIndex = 0;
                        const newCol = bookIndex % columns;
                        const newRow = Math.floor(bookIndex / columns);
                        x = startX + newCol * blockWidth;
                        y = startY + newRow * blockHeight;
                    }

                    const canvas = document.createElement("canvas");

                    JsBarcode(canvas, barcode, {
                        format: "CODE128",
                        width: 1.8,           
                        height: 40,         
                        displayValue: false,
                        margin: 10,         
                        background: "#ffffff",
                        lineColor: "#000000"
                    });

                    const imgData = canvas.toDataURL("image/png", 1.0);
                    let pdfImageHeight = 11;
                    const imgRatio = canvas.width / canvas.height;
                    let pdfImageWidth = pdfImageHeight * imgRatio; 
                    const maxAllowedWidth = blockWidth - 6; 
                    
                    if (pdfImageWidth > maxAllowedWidth) {
                        pdfImageWidth = maxAllowedWidth;
                        pdfImageHeight = pdfImageWidth / imgRatio;
                    }

                    const centeredX = x + ((blockWidth - pdfImageWidth) / 2);
                    doc.addImage(imgData, "PNG", centeredX, y + 4, pdfImageWidth, pdfImageHeight);
                    doc.setFontSize(8);
                    doc.setFont("helvetica", "bold");
                    doc.text(barcode, x + (blockWidth / 2), y + 4 + pdfImageHeight + 4, { align: "center" });
                    bookIndex++;
                });
            });

            const filename = books.length === 1
                ? `book-${(books[0].title || "unknown").replace(/[^a-zA-Z0-9]/g, "_")}-barcodes.pdf`
                : "all-book-barcodes.pdf";

            doc.save(filename);

            PubSub.publish("RECORD_SUCCESS_TOAST", {
                title: "Success",
                message: `Barcode PDF generated for ${books.length} book${books.length > 1 ? "s" : ""}`
            });

        } catch (error) {
            PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Error",
                message: error.message
            });
        } finally {
            downloadState.isLoading = false;
            PubSub.publish("HIDE_GLOBAL_LOADER");
        }
    };

    const downloadAllBarcodes = async () => {
        if (downloadState.isLoading) return;
        downloadState.isLoading = true;
        PubSub.publish("SHOW_GLOBAL_LOADER", { message: "Fetching all books and generating barcode PDF...", type: "progress" });

        try {
            const bookApi = new DataApi("book");
            const response = await bookApi.fetchAll('');
            const allBooks = normalizeListResponse(response.data);
            if (allBooks.length === 0) {
                PubSub.publish("RECORD_ERROR_TOAST", { title: "No Books", message: "No books found in system." });
                return;
            }
            await bulkDownloadBarcodes(allBooks);
        } catch (error) {
            PubSub.publish("RECORD_ERROR_TOAST", { title: "Error", message: error.message });
        } finally {
            downloadState.isLoading = false;
            PubSub.publish("HIDE_GLOBAL_LOADER");
        }
    };

    return {
        authors: authorOptions,
        classifications: classificationOptions,
        publishers: publisherOptions,

        moduleName: "book",
        moduleLabel: "Book",
        apiEndpoint: "book",
        importMatchFields: ["isbn"],

        autoCreateRelated: {
            authors: { endpoint: "author", labelField: "name" },
            classifications: { endpoint: "classification", labelField: "code" },
            publishers: { endpoint: "publisher", labelField: "name" }
        },

        initialFormData: {
            title: "",
            author_id: "",
            classification_id: "",
            classification_category: "",
            classification_code: "",
            classification_type: "",
            classification_from: "",
            classification_to: "",
            publisher_id: "",
            isbn: "",
            inventory_binding: "",
            edition: "",
            publication_year: "",
            pages: "",
            language: "",
            min_age: "",
            max_age: "",
            status: "ACTIVE",
            company_id: "",
            suggested_rack_mapping_id: "",
            is_book_bank: false,
            year_of_edition: "",
            volume: "",
        },
        columns: [
            { field: "title", label: "Title" },
            { field: "author_name", label: "Author" },
            { field: "classification_name", label: "Classification" },
            { field: "publisher_name", label: "Publisher" },
            { field: "isbn", label: "ISBN/ISSN" },
            { field: "edition", label: "Edition" },
            { field: "publication_year", label: "Year" },
            { field: "year_of_edition", label: "Year Of Edition" },
            { field: "volume", label: "Volume" },
            {
                field: "status",
                label: "Status",
                render: (val) => {
                    const isActive = val === true || val === 'ACTIVE';
                    return (
                        <span className={`badge bg-${isActive ? 'success' : 'danger'}`}>
                            {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                    );
                }
            },
        ],
        formFields: [
            {
                name: "title",
                label: "Title",
                type: "text",
                required: true,
                placeholder: "Enter book title",
                colSize: 6,
            },
            {
                name: "author_id",
                label: "Author",
                type: "select",
                isSearchable: true,
                placeholder: "Search by name...",
                options: "authors",
                required: true,
                colSize: 6,
            },
            {
                name: "classification_id",
                label: "Classification",
                type: "select",
                isSearchable: true,
                options: classificationOptions,
                required: true,
                placeholder: "Search classification by name, code or range",
                helpText: "Supports DDC (000-099) and LLC (AC-AZ) classifications.",
                colSize: 6,
                
                onChange: (arg1, arg2, arg3, arg4) => {
                    // 1. Find the setter function
                    const setFormData = [arg1, arg2, arg3, arg4].find(arg => typeof arg === 'function');
                    if (!setFormData) return;

                    // 2. Extract the raw value (works whether it's arg1 or arg2)
                    const rawValue = typeof arg1 === 'string' ? arg2 : arg1;

                    // 3. Extract just the ID string (handles both {value: "12"} and "12")
                    const searchId = rawValue?.value !== undefined ? String(rawValue.value) : String(rawValue);

                    // 4. Look up the ACTUAL option from our pre-loaded array
                    const actualOption = classificationOptions.find(opt => opt.value === searchId);

                    if (actualOption && actualOption.data) {
                        const data = actualOption.data;
                        const rawShelves = externalData.shelf || [];
                        let matchedSubShelfId = "";

                        // Find matching shelf
                        const matched = rawShelves.find(s => {
                            if (s.classification_type !== data.classification_type) return false;

                            if (s.classification_type === 'DDC') {
                                const bFrom = parseFloat(data.classification_from);
                                const bTo = parseFloat(data.classification_to);
                                const sFrom = parseFloat(s.classification_from);
                                const sTo = parseFloat(s.classification_to);
                                return (bFrom >= sFrom && bTo <= sTo);
                            } else {
                                const bFrom = (data.classification_from || "").toUpperCase();
                                const bTo = (data.classification_to || "").toUpperCase();
                                const sFrom = (s.classification_from || "").toUpperCase();
                                const sTo = (s.classification_to || "").toUpperCase();
                                return (bFrom >= sFrom && bTo <= sTo);
                            }
                        });

                        if (matched) matchedSubShelfId = matched.id;

                        // 5. Safely update the form data!
                        setFormData(prev => ({
                            ...prev,
                            classification_id: actualOption.value, // Save the ID
                            classification_category: data.category || '',
                            classification_type: data.classification_type || '',
                            classification_code: data.code || '',
                            classification_from: data.classification_from || '',
                            classification_to: data.classification_to || '',
                            suggested_rack_mapping_id: matchedSubShelfId || prev.suggested_rack_mapping_id
                        }));
                    } else {
                        // Clear fields if user deletes the selection 
                        
                        setFormData(prev => ({
                            ...prev,
                            classification_id: "",
                            classification_category: "",
                            classification_type: "",
                            classification_code: "",
                            classification_from: "",
                            classification_to: ""
                        }));
                    }
                }
            },
            {
                name: "classification_type",
                label: "Classification Type",
                type: "text",
                readOnly: true,
                colSize: 6,
                placeholder: "Auto-filled"
            },
            {
                name: "classification_from",
                label: "Range From",
                type: "text",
                readOnly: true,
                colSize: 3,
                placeholder: "Auto-filled"
            },
            {
                name: "classification_to",
                label: "Range To",
                type: "text",
                readOnly: true,
                colSize: 3,
                placeholder: "Auto-filled"
            },
            {
                name: "publisher_id",
                label: "Publisher",
                type: "select",
                isSearchable: true,
                placeholder: "Search by name...",
                options: "publishers",
                required: false,
                colSize: 6,
            },
            {
                name: "isbn",
                label: "ISBN/ISSN",
                type: "text",
                placeholder: "Enter ISBN/ISSN (max 13 digits)",
                colSize: 6,
                props: {
                    maxLength: 13,
                    pattern: "[0-9]*",
                    inputMode: "numeric",
                    title: "ISBN must be maximum 13 digits (numbers only)"
                },
                validation: {
                    maxLength: 13,
                    pattern: /^[0-9]{0,13}$/,
                    required: false
                },
                errorMessage: "ISBN must be maximum 13 digits (numbers only)",
            },
            {
                name: "min_age",
                label: "Min Age",
                type: "number",
                placeholder: "Enter minimum age",
                colSize: 6,
                props: { min: 0 }
            },
            {
                name: "inventory_binding",
                label: "Inventory Binding",
                type: "select",
                isSearchable: false,
                options: inventoryBindings,
                required: false,
                colSize: 6,
            },
            {
                name: "language",
                label: "Language",
                type: "text",
                placeholder: "Enter language",
                colSize: 6,
            },
            {
                name: "edition",
                label: "Edition",
                type: "text",
                placeholder: "e.g. 1st Edition",
                colSize: 6,
            },
            {
                name: "publication_year",
                label: "Publication Year",
                type: "number",
                placeholder: "e.g. 2023",
                colSize: 6,
            },
            {
                name: "pages",
                label: "Total Pages",
                type: "number",
                placeholder: "Enter page count",
                colSize: 6,
            },
            {
                field: "status",
                label: "Status",
                render: (val) => {
                    const isActive = val === true || val === 'ACTIVE';
                    return (
                        <span className={`badge bg-${isActive ? 'success' : 'danger'}`}>
                            {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                    );
                }
            },
            {
                name: "year_of_edition",
                label: "Year Of Edition",
                type: "text",
                placeholder: "Enter Year of Edition",
                colSize: 6,
            },
            {
                name: "volume",
                label: "Volume",
                type: "text",
                placeholder: "Enter volume",
                colSize: 6,
            },
            ...(isBookBankEnabled
                ? [{
                    name: "is_book_bank",
                    label: "Book Bank",
                    type: "checkbox",
                    required: false,
                    colSize: 6,
                    helpText: "Check if this book is part of the book bank collection"
                  }]
                : [])
        ],
        validationRules: (formData, allBooks, editingBook) => {
            const errors = [];
            if (!formData.title?.trim()) errors.push("Title is required");
            if (!formData.author_id) errors.push("Author is required");
            if (!formData.classification_id) errors.push("Classification is required");
            if (formData.min_age !== undefined && formData.min_age !== null && formData.min_age !== "" && formData.min_age < 0) {
                errors.push("Min age must be non-negative");
            }
            const duplicate = allBooks.find(book => formData.isbn && book.isbn === formData.isbn && book.id !== editingBook?.id);
            if (duplicate) errors.push("Book with this ISBN/ISSN already exists");
            return errors;
        },
        dataDependencies: {
            authors: "author",
            classifications: "classification",
            publishers: "publisher"
        },
        features: {
            showBulkInsert: false,
            showImportExport: true,
            showDetailView: true,
            showSearch: true,
            showColumnVisibility: true,
            showCheckbox: true,
            showActions: true,
            showAddButton: true,
            allowEdit: permissions.allowEdit || true,
            allowDelete: false,
            showImportButton: true,
            showAdvancedFilter: true,
            permissions: permissions,
        },
        filterFields: [
            { name: "title", label: "Title", type: "text" },
            { name: "classification_id", label: "Classification", type: "select", options: classificationOptions },
            { name: "publisher_id", label: "Publisher", type: "select", options: publisherOptions },
            { name: "isbn", label: "ISBN/ISSN", type: "text" },
            {
                name: "is_book_bank",
                label: "",
                type: "select",
                options: [
                    { label: "Book Bank", value: true },
                    { label: "Books", value: false }
                ]
            }
        ],
        lookupNavigation: {
            author_name: { path: "author", idField: "author_id", labelField: "author_name" },
            classification_name: { path: "classification", idField: "classification_id", labelField: "classification_name" },
            publisher_name: { path: "publisher", idField: "publisher_id", labelField: "publisher_name" }
        },
        detailConfig: {
            fields: {
                details: [
                    { key: "title", label: "Title" },
                    { key: "author_name", label: "Author" },
                    { key: "publisher_name", label: "Publisher" },
                    { key: "isbn", label: "ISBN/ISSN" },
                    { key: "classification_name", label: "Classification" },
                    { key: "language", label: "Language" },
                    { key: "edition", label: "Edition" },
                    { key: "publication_year", label: "Publication Year" },
                    { key: "pages", label: "Pages" },
                    { key: "min_age", label: "Min Age" },
                    { key: "inventory_binding", label: "Inventory Binding" },
                    { key: "status", label: "Status" },
                    { key: "year_of_edition", label: "Year Of Edition" },   
                    { key: "volume", label: "Volume" },                     
                ]
            },
            relatedModules: [
                {
                    key: "book_copies",
                    label: "Inventory / Book Copies",
                    api: "book-copy",
                    endpoint: (id) => `?book_id=${id}`,
                    columns: [
                        { key: "itemcallnumber", label: "Call Number" },
                        { key: "status", label: "Status" },
                        { key: "item_price", label: "Price" },
                        { key: "branch_name", label: "Branch Name" }
                    ]
                }
            ]
        },
        importModel: BookModel,
        headerActions: [
          {
            variant: "primary",
            size: "sm",
            icon: downloadState.isLoading ? "fa fa-spinner fa-spin" : "fa-solid fa-barcode", 
            label: downloadState.isLoading ? "Generating PDF..." : "Smart Download Barcodes",
            disabled: downloadState.isLoading,
            onClick: (selectedBooks) => {
              const validBooks = Array.isArray(selectedBooks) 
                ? selectedBooks.filter(b => b && b.id)
                : [];
              if (validBooks.length > 0) {
                bulkDownloadBarcodes(validBooks);
              } else {
                downloadAllBarcodes();
              }
            }
          }
        ],
    };
};